import Link from "next/link";
import Image from "next/image";
import * as React from "react";
import { DateTime } from "luxon";
import styles from "./../styles.module.css";
import { Product, ProductExtra } from "@/types/broadbandProduct";

const Deal: React.FC<{
  data: Product;
  urlReference?: string;
  urlReference2?: string;
  urlReference3?: string;
  onDealClick?: (deal: Product) => void;
}> = ({ data, urlReference, urlReference2, urlReference3, onDealClick }) => {
  const getUrl = () => {
    if (!urlReference) {
      return data.outlink;
    }

    let param = data.network.relationship_type === "direct" ? "pref1" : "ref1";
    let localUrl = `${data.outlink}&${param}=${urlReference}`;

    if (urlReference2) {
      localUrl += `&ref2=${urlReference2}`;
    }

    if (urlReference3) {
      localUrl += `&ref3=${urlReference3}`;
    }

    return localUrl;
  };

  const offerExpiry = React.useCallback(() => {
    if (data.offer_expiry === "") return null;

    const now = DateTime.now();
    const expiry = DateTime.fromISO(data.offer_expiry);
    const dateDiff = expiry.diff(now, ["days"]).toObject();

    if (dateDiff.days === undefined) return null;

    return (
      <>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          fill="currentColor"
          className="bi bi-calendar2-week"
          viewBox="0 0 16 16"
        >
          <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM2 2a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1H2z" />
          <path d="M2.5 4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5V4zM11 7.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm-3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm-5 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1z" />
        </svg>
        {Math.floor(dateDiff.days) === 0 ? (
          <span className="text-danger">
            <strong className="ps-2">Ends Today</strong>
          </span>
        ) : Math.floor(dateDiff.days) === 1 ? (
          <>
            <strong className="ps-2">Ends in:</strong>&nbsp;{" "}
            <span className={styles.text__amber}>1 Day</span>
          </>
        ) : (
          <>
            <strong className="ps-2">Ends in:</strong>&nbsp;{" "}
            {Math.floor(dateDiff.days)} Days
          </>
        )}
      </>
    );
  }, [data.offer_expiry]);

  React.useEffect(() => {
    offerExpiry();
  }, [offerExpiry]);

  return (
    <div className={`${styles.deal__container} mb-4 w-100 ms-lg-5 ps-4 pb-3 `}>
      <div className="d-flex">
        <div className=" col-md-8"></div>
        <div className="col-md-4 col-12">
          <div className={`${styles.deal_perk} `}>
            <span className={`${styles["deal__provider-benefits"]}`}>
              {data.extras.map((value: ProductExtra, index) => {
                return index === data.extras.length - 1
                  ? value.name
                  : `${value.name} + `;
              })}
            </span>
          </div>
        </div>
      </div>
      <div
        className={`d-flex flex-wrap${
          data.extras.length === 0 ? " mt-1 mt-md-4" : ""
        }`}
      >
        <div className="col-12 col-md-8 pe-4 pe-md-0">
          <div className={styles["deal__provider-header"]}>
            <div className={`${styles["deal__provider-header-content"]}`}>
              <div className={styles.deal__icon}>
                {data.network.image_url && (
                  <Image
                    fill={true}
                    src={data.network.image_url}
                    className={styles["deal__icon-image"]}
                    alt={data.network.name}
                  />
                )}
              </div>
              <h3 className={`${styles.deal__title} pe-md-5`}>{data.name}</h3>
              <div className="ms-md-auto m-3 ">{offerExpiry()}</div>
            </div>
          </div>
          <div
            className={`${styles["deal__selling-points"]} flex-wrap pt-4 pb-4`}
          >
            <div
              className={`${styles["deal__selling-points-box"]} text-center text-md-start`}
            >
              <div className="text-end">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  fill="currentColor"
                  className="bi bi-speedometer2 "
                  viewBox="0 0 16 16"
                >
                  <path d="M8 4a.5.5 0 0 1 .5.5V6a.5.5 0 0 1-1 0V4.5A.5.5 0 0 1 8 4zM3.732 5.732a.5.5 0 0 1 .707 0l.915.914a.5.5 0 1 1-.708.708l-.914-.915a.5.5 0 0 1 0-.707zM2 10a.5.5 0 0 1 .5-.5h1.586a.5.5 0 0 1 0 1H2.5A.5.5 0 0 1 2 10zm9.5 0a.5.5 0 0 1 .5-.5h1.5a.5.5 0 0 1 0 1H12a.5.5 0 0 1-.5-.5zm.754-4.246a.389.389 0 0 0-.527-.02L7.547 9.31a.91.91 0 1 0 1.302 1.258l3.434-4.297a.389.389 0 0 0-.029-.518z" />
                  <path
                    fill-rule="evenodd"
                    d="M0 10a8 8 0 1 1 15.547 2.661c-.442 1.253-1.845 1.602-2.932 1.25C11.309 13.488 9.475 13 8 13c-1.474 0-3.31.488-4.615.911-1.087.352-2.49.003-2.932-1.25A7.988 7.988 0 0 1 0 10zm8-7a7 7 0 0 0-6.603 9.329c.203.575.923.876 1.68.63C4.397 12.533 6.358 12 8 12s3.604.532 4.923.96c.757.245 1.477-.056 1.68-.631A7 7 0 0 0 8 3z"
                  />
                </svg>
              </div>
              <h3
                className={`${styles["deal__selling-points-title"]} text-center`}
              >
                {data.download_speed}
                <span style={{ fontSize: "16px" }}>Mbps</span>
              </h3>
              <p className={`mb-0 text-center ${styles.deal__description}`}>
                Average speed
              </p>
            </div>

            <div
              className={`${styles["deal__selling-points-box"]} text-center text-md-start`}
            >
              <div className="text-end">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  className="bi bi-calendar4-event"
                  viewBox="0 0 16 16"
                >
                  <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM2 2a1 1 0 0 0-1 1v1h14V3a1 1 0 0 0-1-1H2zm13 3H1v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V5z" />
                  <path d="M11 7.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1z" />
                </svg>
              </div>
              <h3
                className={`${styles["deal__selling-points-title"]} text-center`}
              >
                {data.contract_length}
              </h3>
              <p className={`mb-0 text-center ${styles.deal__description}`}>
                Month Contract
              </p>
            </div>

            <div
              className={`${styles["deal__selling-points-box"]} text-center text-md-start`}
            >
              <div className="text-end">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  fill="currentColor"
                  className="bi bi-cash-stack"
                  viewBox="0 0 16 16"
                >
                  <path d="M1 3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1H1zm7 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
                  <path d="M0 5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V5zm3 0a2 2 0 0 1-2 2v4a2 2 0 0 1 2 2h10a2 2 0 0 1 2-2V7a2 2 0 0 1-2-2H3z" />
                </svg>
              </div>
              <h3
                className={`${styles["deal__selling-points-title"]} text-center`}
              >
                &pound;{data.setup_fee}
              </h3>
              <p className={`mb-0 text-center ${styles.deal__description}`}>
                Set up cost
              </p>
            </div>

            <div
              className={`${styles["deal__selling-points-box"]} text-center text-md-start`}
            >
              <div className="text-end">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  fill="currentColor"
                  className="bi bi-cash-stack"
                  viewBox="0 0 16 16"
                >
                  <path d="M1 3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1H1zm7 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
                  <path d="M0 5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V5zm3 0a2 2 0 0 1-2 2v4a2 2 0 0 1 2 2h10a2 2 0 0 1 2-2V7a2 2 0 0 1-2-2H3z" />
                </svg>
              </div>
              <h3
                className={`${styles["deal__selling-points-title"]} text-center`}
              >
                &pound;{data.monthly_cost}
              </h3>
              <p className={`mb-0 text-center ${styles.deal__description}`}>
                Monthly cost
              </p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4 d-flex flex-column justify-content-center align-items-md-center ">
          <div className="mx-auto">
            <Link
              onClick={() =>
                onDealClick !== undefined ? onDealClick(data) : null
              }
              href={getUrl()}
              target="_blank"
              rel="noreferrer"
              id={`BB-${data.name}`}
              className={`${styles["broadband-affiliate__button"]} button d-flex align-items-center text-decoration-none`}
            >
              See Deal
            </Link>
          </div>
          <div className="mx-auto mt-3">
            <Link
              href="/journey/broadband"
              rel="noreferrer"
              className={`${styles["broadband-affiliate__link"]}`}
            >
              Request a Callback
            </Link>
          </div>
          <div className="mx-auto mt-3">
            <p className={`mb-0 text-center ${styles.prices}`}>
              Prices may change during contract
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Deal;
